// src/App.tsx
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { DebugCapabilities } from './components/DebugCapabilities'; // ✅ ADDED
import api from './services/api';
import MainLayout from './components/layout/MainLayout';
import { LoadingProvider } from './contexts/LoadingContext';


// Auth
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Dashboard
import RoleBasedDashboard from './components/dashboard/RoleBasedDashboard';
import {
  DashboardAdmin,
  DashboardDekan,
  DashboardObserver,
  DashboardPelapor,
  DashboardWarek,
} from './pages/dashboard';

// Laporan
import LaporanList from './pages/laporan/LaporanList';
import Arsip from './pages/laporan/Arsip';
import LaporanCreate from './pages/laporan/LaporanCreate';
import LaporanDetail from './pages/laporan/LaporanDetail';
import LaporanEdit from './pages/laporan/LaporanEdit';

// Validasi
import ValidasiLevel1 from './pages/validasi/ValidasiLevel1';
import ValidasiLevel2 from './pages/validasi/ValidasiLevel2';
import ValidasiWorkflow from './pages/validasi/ValidasiWorkflow';

// RAPIM
import RapimCalendar from './pages/rapim/RapimCalendar';
import RapimCreate from './pages/rapim/RapimCreate';
import RapimDetail from './pages/rapim/RapimDetail';
import RapimKeputusan from './pages/rapim/RapimKeputusan';
import RapimPresentasi from './pages/rapim/RapimPresentasi';

// Monitoring
import MonitoringDashboard from './pages/monitoring/MonitoringDashboard';
import TindakLanjutDetail from './pages/monitoring/TindakLanjutDetail';

// Reports
import ReportBuilder from './pages/reports/ReportBuilder';

// Admin
import UserManagement from './pages/admin/UserManagement';
import RoleManagement from './pages/admin/RoleManagement';
import UnitManagement from './pages/admin/UnitManagement';
import MasterData from './pages/admin/MasterData';
import RoleScopes from './pages/admin/RoleScopes';
import SystemSettings from './pages/admin/SystemSettings';
import AuditLogs from './pages/admin/AuditLogs';
import WorkflowManager from './pages/admin/WorkflowManager';
import RoleCapabilityManager from './pages/admin/RoleCapabilityManager';

// Profile
import Profile from './pages/profile/Profile';
import ChangePassword from './pages/profile/ChangePassword';
import UserSettings from './pages/profile/UserSettings';
import NotificationsPage from './pages/profile/Notifications';

// ============ ROUTE GUARDS ============

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const CapabilityRoute: React.FC<{
  children: React.ReactNode;
  capabilities?: string[];
  requireAny?: boolean;
  pageName?: string;
}> = ({ children, capabilities = [], requireAny = true, pageName = 'halaman ini' }) => {
  const { user } = useAuthStore();
  const caps = JSON.parse(localStorage.getItem('capabilities') || '[]');
  const userRole = (localStorage.getItem('user_role') || '').toLowerCase();

  // Admin selalu bisa akses
  if (userRole === 'admin') return <>{children}</>;

  // Jika tidak ada capability requirement, izinkan
  if (capabilities.length === 0) return <>{children}</>;

  const hasAccess = requireAny
    ? capabilities.some(cap => caps.includes(cap))
    : capabilities.every(cap => caps.includes(cap));

  if (!hasAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-black text-neutral-text-primary mb-2">
            Akses Terbatas
          </h2>
          <p className="text-sm text-neutral-text-secondary mb-4">
            Anda tidak memiliki izin untuk mengakses {pageName}.
          </p>
          <div className="p-4 bg-neutral-bg rounded-xl border border-neutral-border mb-6 text-left">
            <p className="text-xs font-semibold text-neutral-text-secondary uppercase tracking-wider mb-2">
              Informasi Akun
            </p>
            <div className="space-y-1 text-sm">
              <p><span className="text-neutral-text-secondary">Nama:</span> <span className="font-medium">{user?.name || '-'}</span></p>
              <p><span className="text-neutral-text-secondary">Role:</span> <span className="font-medium">{user?.role?.display_name || '-'}</span></p>
            </div>
            <div className="mt-3 pt-3 border-t border-neutral-border">
              <p className="text-xs text-neutral-text-secondary mb-1">Capability yang dibutuhkan:</p>
              <div className="flex flex-wrap gap-1">
                {capabilities.map(cap => (
                  <span key={cap} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-mono rounded">
                    {cap}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 text-sm font-medium text-neutral-text-secondary hover:text-neutral-text-primary bg-neutral-bg rounded-lg transition-colors"
            >
              Kembali
            </button>
            <a
              href="/dashboard"
              className="px-4 py-2 text-sm font-semibold text-white bg-teal-main hover:bg-teal-dark rounded-lg transition-colors"
            >
              Ke Dashboard
            </a>
          </div>
          <p className="text-xs text-neutral-text-secondary mt-6">
            Jika Anda merasa ini kesalahan, hubungi administrator untuk mendapatkan akses.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const GuestRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};



// ============ IDLE TIMEOUT MANAGER ============
// Komponen ini mengelola logout otomatis berdasarkan pengaturan session_lifetime.
const IdleTimeoutManager: React.FC = () => {
  const { isAuthenticated, logout } = useAuthStore();
  const [sessionLifetimeMs, setSessionLifetimeMs] = useState<number>(120 * 60 * 1000); // default 120 menit

  useEffect(() => {
    if (!isAuthenticated) return;

    // Ambil pengaturan session_lifetime dari backend
    api.get('/admin/settings/session_lifetime')
      .then((res) => {
        const value = res.data?.data?.value ?? res.data?.value;
        const menit = parseInt(value ?? '120', 10);
        if (!isNaN(menit) && menit > 0) {
          setSessionLifetimeMs(menit * 60 * 1000);
        }
      })
      .catch(() => {
        // Gunakan nilai default jika gagal mengambil dari backend
      });
  }, [isAuthenticated]);

  // Logout ke backend sekaligus reset state lokal
  const handleTimeout = React.useCallback(async () => {
    try {
      await api.post('/logout');
    } catch {
      // Abaikan error logout API, tetap clear state lokal
    }
    logout();
  }, [logout]);

  useIdleTimeout({
    timeoutMs: sessionLifetimeMs,
    onTimeout: handleTimeout,
    enabled: isAuthenticated,
  });

  return null;
};

// ============ APP ============

const App: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const isDev = import.meta.env.MODE === 'development';

  return (
    <LoadingProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { borderRadius: '12px', fontSize: '14px' },
          }}
        />

        {/* Pengelola logout otomatis — tidak merender UI apapun */}
        <IdleTimeoutManager />

        <Routes>
          {/* ============ GUEST ROUTES ============ */}
          <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
          <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
          <Route path="/reset-password" element={<GuestRoute><ResetPassword /></GuestRoute>} />

          {/* ============ PROTECTED ROUTES ============ */}
          <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>

            {/* Dashboard (per role) */}
            <Route path="/dashboard" element={<RoleBasedDashboard />} />
            <Route path="/dashboard/warek" element={<DashboardWarek />} />
            <Route path="/dashboard/dekan" element={<DashboardDekan />} />
            <Route path="/dashboard/pelapor" element={<DashboardPelapor />} />
            <Route path="/dashboard/observer" element={<DashboardObserver />} />
            <Route path="/dashboard/admin" element={<DashboardAdmin />} />

            {/* Laporan Masalah */}
            <Route path="/laporan" element={<LaporanList />} />
            <Route
              path="/arsip"
              element={(
                <CapabilityRoute capabilities={['archive']} pageName="Arsip Laporan">
                  <Arsip />
                </CapabilityRoute>
              )}
            />
            <Route path="/laporan/buat" element={<LaporanCreate />} />
            <Route path="/laporan/:id" element={<LaporanDetail />} />
            <Route path="/laporan/:id/edit" element={<LaporanEdit />} />

            {/* Validasi */}
            <Route path="/validasi/level1" element={<ValidasiLevel1 />} />
            <Route path="/validasi/level2" element={<ValidasiLevel2 />} />
            <Route path="/validasi/workflow" element={<ValidasiWorkflow />} />

            {/* RAPIM */}
            <Route path="/rapim" element={<RapimCalendar />} />
            <Route path="/rapim/buat" element={<RapimCreate />} />
            <Route path="/rapim/:id" element={<RapimDetail />} />
            <Route path="/rapim/:id/keputusan" element={<RapimKeputusan />} />
            <Route path="/rapim/:id/presentasi" element={<RapimPresentasi />} />

            {/* Monitoring */}
            <Route
              path="/monitoring"
              element={
                <CapabilityRoute capabilities={['access_monitoring']} pageName="Dashboard Monitoring">
                  <MonitoringDashboard />
                </CapabilityRoute>
              }
            />
            <Route path="/tindak-lanjut/:id" element={<TindakLanjutDetail />} />

            {/* Reports & Analytics */}
            <Route path="/reports/builder" element={<ReportBuilder />} />

            {/* Admin */}
            <Route path="/admin/users" element={
              <CapabilityRoute capabilities={['manage_users']} pageName="Manajemen User">
                <UserManagement />
              </CapabilityRoute>
            } />
            <Route path="/admin/roles" element={
              <CapabilityRoute capabilities={['manage_users']} pageName="Role Management">
                <RoleManagement />
              </CapabilityRoute>
            } />
            <Route path="/admin/units" element={
              <CapabilityRoute capabilities={['manage_units']} pageName="Unit Organisasi">
                <UnitManagement />
              </CapabilityRoute>
            } />
            <Route path="/admin/master" element={
              <CapabilityRoute capabilities={['manage_master_data']} pageName="Master Data">
                <MasterData />
              </CapabilityRoute>
            } />
            <Route path="/admin/standar" element={
              <CapabilityRoute capabilities={['manage_master_data']} pageName="Master Data">
                <MasterData />
              </CapabilityRoute>
            } />
            <Route path="/admin/role-scopes" element={
              <CapabilityRoute capabilities={['manage_users']} pageName="Role Scopes">
                <RoleScopes />
              </CapabilityRoute>
            } />
            <Route path="/admin/settings" element={
              <CapabilityRoute capabilities={['manage_settings']} pageName="Pengaturan Sistem">
                <SystemSettings />
              </CapabilityRoute>
            } />
            <Route path="/admin/logs" element={
              <CapabilityRoute capabilities={['view_activity_log']} pageName="Log Aktivitas">
                <AuditLogs />
              </CapabilityRoute>
            } />
            <Route path="/admin/workflow" element={
              <CapabilityRoute capabilities={['manage_settings']} pageName="Workflow Manager">
                <WorkflowManager />
              </CapabilityRoute>
            } />
            <Route path="/admin/capabilities" element={
              <CapabilityRoute capabilities={['manage_users']} pageName="Role Capabilities">
                <RoleCapabilityManager />
              </CapabilityRoute>
            } />

            {/* Profile */}
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<UserSettings />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/notifications" element={<NotificationsPage />} />

          </Route>

          {/* ============ REDIRECTS ============ */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>

        {/* ✅ ADDED: Debug panel (hanya muncul di development + saat login) */}
        {isDev && isAuthenticated && <DebugCapabilities />}
      </BrowserRouter>
    </LoadingProvider>
  );
};

export default App;
